import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, expiryStatus, STATUS_META, initials } from "@/lib/helpers";
import { openFile, AttachmentList } from "@/components/Attachments";
import SignaturePad from "@/components/SignaturePad";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { LogOut, FileDown, ClipboardList, PenLine, HardHat, GraduationCap, Stethoscope, Shirt, Truck, Wrench, Paperclip, FolderOpen, Plane, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function Section({ icon: Icon, title, children, testid }) {
  return (
    <Card className="p-4 sm:p-5" data-testid={testid}>
      <h2 className="font-display font-bold flex items-center gap-2 mb-3"><Icon className="h-4 w-4 text-accent" /> {title}</h2>
      {children}
    </Card>
  );
}

function Row({ label, value, expiry }) {
  const st = expiry ? expiryStatus(expiry) : null;
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b last:border-0 text-sm">
      <span className="min-w-0 truncate">{label}</span>
      <span className="flex items-center gap-2 shrink-0">
        <span className="font-mono text-xs">{value}</span>
        {st && st !== "sin_fecha" && <Badge className={`${STATUS_META[st].cls} text-[10px]`}>{STATUS_META[st].label}</Badge>}
      </span>
    </div>
  );
}

export default function WorkerPortal() {
  const { user, logout } = useAuth();
  const [emp, setEmp] = useState(null);
  const [files, setFiles] = useState([]);
  const [signing, setSigning] = useState(null);
  const [settings, setSettings] = useState(null);
  const [docs, setDocs] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [vacBalance, setVacBalance] = useState(null);
  const [vacForm, setVacForm] = useState({ start_date: "", end_date: "", note: "" });

  const load = () => {
    api.get("/me/employee").then((r) => setEmp(r.data)).catch(() => setEmp(false));
    api.get("/me/attachments").then((r) => setFiles(r.data)).catch(() => {});
    api.get("/me/documents").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/me/vacations").then((r) => setVacations(r.data)).catch(() => {});
    api.get("/me/vacation-balance").then((r) => setVacBalance(r.data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const download = async (path, fallback) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const m = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = m ? m[1] : fallback; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar"); }
  };

  const sign = async (dataUrl, signer) => {
    try {
      const r = await api.post("/me/epi-receipt", { epi_ids: signing.map((p) => p.id), signature: dataUrl, signer_name: signer });
      toast.success("Entrega de EPIs firmada");
      setSigning(null); load();
      openFile(r.data.id, r.data.original_filename);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const requestVacation = async () => {
    if (!vacForm.start_date || !vacForm.end_date) return toast.error("Indica las fechas de inicio y fin");
    try {
      await api.post("/me/vacations", vacForm);
      toast.success("Solicitud enviada. Queda pendiente de aprobación.");
      setVacForm({ start_date: "", end_date: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (emp === null) return <div className="p-6 space-y-3 max-w-2xl mx-auto">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>;
  if (emp === false) return <div className="p-10 text-center text-sm">No se encontró tu ficha. Contacta con el administrador.</div>;

  const pending = emp.epis.filter((p) => p.delivery_date && !p.signed_at);
  const receipts = files.filter((f) => f.kind === "recibo_epi");
  const certs = files.filter((f) => f.kind !== "recibo_epi" && f.section !== "documents");
  const payslips = docs.filter((d) => d.doc_type === "nomina");
  const timesheets = docs.filter((d) => d.doc_type === "registro_jornada");
  const VAC_STATUS = {
    pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-800" },
    aprobada: { label: "Aprobada", cls: "bg-emerald-100 text-emerald-800" },
    rechazada: { label: "Rechazada", cls: "bg-red-100 text-red-700" },
  };

  return (
    <div className="min-h-screen bg-background" data-testid="worker-portal">
      <header className="bg-primary text-primary-foreground px-4 py-3 flex items-center gap-3 sticky top-0 z-30">
        {settings?.logo ? <img src={settings.logo} alt="" className="h-8 w-8 rounded bg-white object-contain p-0.5" /> : <HardHat className="h-6 w-6" />}
        <div className="min-w-0 flex-1">
          <p className="font-display font-bold text-sm truncate">{settings?.company_name || "Portal del trabajador"}</p>
          <p className="text-[11px] text-slate-300 truncate">{user?.email}</p>
        </div>
        <Button variant="ghost" size="sm" className="text-slate-200 hover:text-white hover:bg-white/10" onClick={logout} data-testid="worker-logout-button">
          <LogOut className="h-4 w-4" />
        </Button>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-4">
        <Card className="p-5 flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center font-semibold text-accent text-lg overflow-hidden shrink-0">
            {emp.photo ? <img src={emp.photo} alt="" className="h-full w-full object-cover" /> : initials(emp.name)}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-xl font-extrabold truncate" data-testid="worker-name">{emp.name}</h1>
            <p className="text-sm text-muted-foreground">{emp.position || "—"} · DNI {emp.dni}</p>
            <p className="text-xs text-muted-foreground">Alta {fmtDate(emp.hire_date)} · Tallas: ropa {emp.clothing_size || "—"}, calzado {emp.shoe_size || "—"}</p>
          </div>
        </Card>

        {pending.length > 0 && (
          <Card className="p-4 border-amber-300 bg-amber-50 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="worker-pending-signature">
            <div className="flex-1 text-sm text-amber-900">
              <p className="font-semibold">Tienes {pending.length} EPI(s) pendientes de firmar</p>
              <p className="text-xs">{pending.map((p) => p.etype).join(", ")}</p>
            </div>
            <Button onClick={() => setSigning(pending)} data-testid="worker-sign-button"><PenLine className="h-4 w-4 mr-2" /> Firmar entrega</Button>
          </Card>
        )}

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => download(`/employees/${emp.id}/pdf`, "ficha.pdf")} data-testid="worker-pdf-button">
            <FileDown className="h-4 w-4 mr-2" /> Mi ficha PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => download(`/employees/${emp.id}/epi-sheet`, "ficha_epi.pdf")} data-testid="worker-epi-sheet-button">
            <ClipboardList className="h-4 w-4 mr-2" /> Ficha de EPIs
          </Button>
        </div>

        <Section icon={HardHat} title="Mis EPIs" testid="worker-epis">
          {emp.epis.length === 0 && <p className="text-sm text-muted-foreground">Sin EPIs registrados.</p>}
          {emp.epis.map((p) => (
            <div key={p.id} className="py-2 border-b last:border-0 text-sm flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate">{p.etype} {p.brand && <span className="text-muted-foreground">· {p.brand}</span>}</p>
                <p className="text-xs text-muted-foreground">Entrega {fmtDate(p.delivery_date)} · Caduca {p.expiry ? fmtDate(p.expiry) : "por uso"}</p>
              </div>
              {p.signed_at ? <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Firmado</Badge> : <Badge className="bg-amber-100 text-amber-800 text-[10px]">Sin firma</Badge>}
            </div>
          ))}
        </Section>

        <Section icon={GraduationCap} title="Cursos PRL" testid="worker-courses">
          {emp.prl_courses.map((c) => <Row key={c.id} label={c.name} value={c.expiry ? fmtDate(c.expiry) : "sin fecha"} expiry={c.expiry} />)}
        </Section>

        <Section icon={Stethoscope} title="Reconocimientos médicos" testid="worker-medical">
          {emp.medical_checks.map((m) => <Row key={m.id} label={`${m.result} · ${fmtDate(m.date)}`} value={fmtDate(m.expiry)} expiry={m.expiry} />)}
        </Section>

        <Section icon={Shirt} title="Ropa y calzado" testid="worker-clothing">
          {emp.clothing?.length ? emp.clothing.map((c) => <Row key={c.id} label={`${c.item} · talla ${c.size || "—"} × ${c.qty || 1}`} value={fmtDate(c.delivery_date)} />) : <p className="text-sm text-muted-foreground">Sin entregas.</p>}
        </Section>

        {(emp.vehicles.length > 0 || emp.tools.length > 0) && (
          <Section icon={Truck} title="Vehículo y herramientas" testid="worker-assets">
            {emp.vehicles.map((v) => <Row key={v.id} label={`${v.brand} ${v.model} · ${v.plate}`} value={v.next_itv ? `ITV ${fmtDate(v.next_itv)}` : ""} expiry={v.next_itv} />)}
            {emp.tools.map((t) => <Row key={t.id} label={<span className="flex items-center gap-1"><Wrench className="h-3 w-3" /> {t.name}</span>} value={fmtDate(t.delivery_date)} />)}
          </Section>
        )}

        <Section icon={FolderOpen} title="Mis nóminas y registro de jornada" testid="worker-documents">
          {payslips.length === 0 && timesheets.length === 0 && <p className="text-sm text-muted-foreground">Todavía no hay documentos publicados.</p>}
          {payslips.length > 0 && (
            <>
              <p className="text-xs font-semibold text-muted-foreground mb-1">Nóminas</p>
              <div className="space-y-1">
                {payslips.map((d) => (
                  <div key={d.id} className="flex items-center gap-2">
                    <span className="text-[11px] font-mono text-muted-foreground w-16 shrink-0">{d.period || "—"}</span>
                    <div className="flex-1 min-w-0"><AttachmentList files={[d]} isAdmin={false} onDeleted={load} /></div>
                  </div>
                ))}
              </div>
            </>
          )}
          {timesheets.length > 0 && (
            <>
              <p className="text-xs font-semibold text-muted-foreground mt-3 mb-1">Registro de jornada</p>
              <div className="space-y-1">
                {timesheets.map((d) => (
                  <div key={d.id} className="flex items-center gap-2">
                    <span className="text-[11px] font-mono text-muted-foreground w-16 shrink-0">{d.period || "—"}</span>
                    <div className="flex-1 min-w-0"><AttachmentList files={[d]} isAdmin={false} onDeleted={load} /></div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Section>

        <Section icon={Plane} title="Mis vacaciones" testid="worker-vacations">
          {vacBalance && (
            <div className="mb-4 grid grid-cols-3 gap-3 rounded-lg border bg-slate-50/60 p-3 text-center" data-testid="worker-vacation-balance">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Disponibles</p>
                <p className="font-display text-xl font-extrabold">{vacBalance.remaining}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Consumidos</p>
                <p className="font-display text-xl font-extrabold">{vacBalance.used}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Pendientes</p>
                <p className="font-display text-xl font-extrabold text-amber-700">{vacBalance.pending}</p>
              </div>
              <p className="col-span-3 text-[11px] text-muted-foreground">
                {vacBalance.quota} días naturales al año · {vacBalance.year}
              </p>
            </div>
          )}
          {vacations.length === 0 && <p className="text-sm text-muted-foreground">Sin periodos registrados.</p>}
          {vacations.map((v) => (
            <div key={v.id} className="py-2 border-b last:border-0 text-sm flex items-center justify-between gap-2" data-testid={`worker-vacation-${v.id}`}>
              <div className="min-w-0">
                <p className="truncate">{fmtDate(v.start_date)} → {fmtDate(v.end_date)}</p>
                <p className="text-xs text-muted-foreground">{v.days} días{v.note ? ` · ${v.note}` : ""}</p>
              </div>
              <Badge className={`${VAC_STATUS[v.status]?.cls} text-[10px]`}>{VAC_STATUS[v.status]?.label || v.status}</Badge>
            </div>
          ))}
          <div className="mt-4 rounded-lg border p-3 bg-slate-50/60 space-y-2" data-testid="worker-vacation-form">
            <p className="text-sm font-medium flex items-center gap-1.5"><Plus className="h-4 w-4 text-accent" /> Solicitar vacaciones</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Desde</Label>
                <Input type="date" className="bg-white" value={vacForm.start_date}
                  onChange={(e) => setVacForm((f) => ({ ...f, start_date: e.target.value }))} data-testid="worker-vacation-start" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hasta</Label>
                <Input type="date" className="bg-white" value={vacForm.end_date}
                  onChange={(e) => setVacForm((f) => ({ ...f, end_date: e.target.value }))} data-testid="worker-vacation-end" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Motivo o nota (opcional)</Label>
              <Input className="bg-white" value={vacForm.note}
                onChange={(e) => setVacForm((f) => ({ ...f, note: e.target.value }))} data-testid="worker-vacation-note" />
            </div>
            <Button className="w-full" onClick={requestVacation} data-testid="worker-vacation-submit">Enviar solicitud</Button>
          </div>
        </Section>

        <Section icon={Paperclip} title="Certificados y recibos" testid="worker-files">
          {certs.length === 0 && receipts.length === 0 && <p className="text-sm text-muted-foreground">Sin documentos.</p>}
          {certs.length > 0 && <p className="text-xs font-semibold text-muted-foreground mb-1">Certificados</p>}
          <AttachmentList files={certs} isAdmin={false} onDeleted={load} />
          {receipts.length > 0 && <p className="text-xs font-semibold text-muted-foreground mt-3 mb-1">Recibos de EPIs firmados</p>}
          <AttachmentList files={receipts} isAdmin={false} onDeleted={load} />
        </Section>
      </main>

      <SignaturePad open={!!signing} title="Firma de entrega de EPIs" employeeName={emp.name}
        items={(signing || []).map((p) => `${p.etype}${p.delivery_date ? ` (entregado ${fmtDate(p.delivery_date)})` : ""}`)}
        onClose={() => setSigning(null)} onConfirm={sign} />
    </div>
  );
}
